import { Link } from "@tanstack/react-router";
import type { CanvasFeature } from "./map-canvas";

/**
 * The text version of whatever the map is currently showing — always visible, not a
 * screen-reader-only afterthought. It is the map's keyboard and assistive-tech path
 * (real links, in reading order), and it is what a crawler indexes.
 *
 * Capped because a zoomed-out county view can hold hundreds of features; the count
 * line always states the true total so the cap is never silent.
 */
const MAX_ROWS = 60;

export function MapFeatureList({
  features,
  title,
  emptyHint,
  onHighlight,
}: {
  features: CanvasFeature[];
  title: string;
  emptyHint: string;
  onHighlight?: (id: string) => void;
}) {
  const shown = features.slice(0, MAX_ROWS);
  const hidden = features.length - shown.length;

  return (
    <section aria-label={title} className="text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="label-caps text-muted-foreground">{title}</h2>
        <p className="text-xs text-muted-foreground">
          {features.length === 0
            ? "Nothing in view"
            : `${features.length} in view${hidden > 0 ? ` · showing the first ${shown.length}` : ""}`}
        </p>
      </div>

      {features.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">{emptyHint}</p>
      ) : (
        <ul className="mt-2 divide-y divide-border border-y border-border sm:columns-2 sm:gap-8 sm:[&>li]:break-inside-avoid">
          {shown.map((f) => (
            <li key={f.id}>
              <Link
                to={f.href}
                onMouseEnter={onHighlight ? () => onHighlight(f.id) : undefined}
                onFocus={onHighlight ? () => onHighlight(f.id) : undefined}
                className="flex items-center justify-between gap-3 py-2.5 transition-surface hover:text-primary"
              >
                <span className="truncate text-foreground">{f.name}</span>
                <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-2.5 ring-1 ring-foreground/25"
                    style={{ backgroundColor: f.bucket.fill }}
                  />
                  {f.bucket.label}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hidden > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {hidden} more are in view but not listed. Zoom in to narrow the list.
        </p>
      )}
    </section>
  );
}
